import React, { useState } from 'react';

import { Controller } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useParams } from 'react-router-dom';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Box } from '@mui/material';

import { EventSelectionStep } from './components/EventSelectionStep';
import { useEventInvite } from './hooks/useEventInvite';
import Button from 'components/Button';
import {
  OnboardingContentCenter,
  OnboardingForm,
  OnboardingInputsContainer,
  OnboardingLayout,
  OnboardingNameSurnameContainer,
} from 'components/OnboardingComponents';
import Text from 'components/Text';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import 'react-phone-input-2/lib/style.css';

export const EventInvite: React.FC = () => {
  const { invite_uuid } = useParams<{ invite_uuid: string }>();
  const theme = useThemeContext();

  const {
    currentStep,
    loading,
    userEmail,
    upcomingEvents,
    selectedEventId,
    setSelectedEventId,
    emailForm,
    passwordForm,
    registerForm,
    handleEmailSubmit,
    handlePasswordSubmit,
    handleRegisterSubmit,
    subscribeToEvent,
    setCurrentStep,
  } = useEventInvite(invite_uuid || '');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // Email Step
  if (currentStep === 'email') {
    return (
      <OnboardingLayout>
        <OnboardingForm onSubmit={emailForm.handleSubmit(handleEmailSubmit)}>
          <OnboardingContentCenter>
            <Text size="medium" color="textPrimary">
              Digite seu e-mail para continuar
            </Text>

            <OnboardingInputsContainer>
              <Controller
                name="email"
                control={emailForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    variant="outlined"
                    fullWidth
                    error={!!emailForm.formState.errors.email}
                    helperText={emailForm.formState.errors.email?.message}
                    size="medium"
                    autoFocus
                  />
                )}
              />
            </OnboardingInputsContainer>

            <Button type="submit" variant="primary" fullWidth loading={loading}>
              {loading ? 'Verificando...' : 'Continuar'}
            </Button>
          </OnboardingContentCenter>
        </OnboardingForm>
      </OnboardingLayout>
    );
  }

  // Password Step (for existing users)
  if (currentStep === 'password') {
    return (
      <OnboardingLayout>
        <OnboardingForm onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}>
          <OnboardingContentCenter>
            <Text size="medium" color="textPrimary">
              Digite sua senha
            </Text>
            <div style={{ marginTop: '0.5rem' }}>
              <Text size="small" color="textSecondary">
                {userEmail}
              </Text>
            </div>

            <OnboardingInputsContainer>
              <Controller
                name="password"
                control={passwordForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    error={!!passwordForm.formState.errors.password}
                    helperText={passwordForm.formState.errors.password?.message}
                    size="medium"
                    autoFocus
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={toggleShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </OnboardingInputsContainer>

            <Button type="submit" variant="primary" fullWidth loading={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>

            <Button
              type="button"
              variant="minimal"
              size="medium"
              onClick={() => setCurrentStep('email')}
            >
              Voltar
            </Button>
          </OnboardingContentCenter>
        </OnboardingForm>
      </OnboardingLayout>
    );
  }

  // Register Step (for new users)
  if (currentStep === 'register') {
    return (
      <OnboardingLayout>
        <OnboardingForm onSubmit={registerForm.handleSubmit(handleRegisterSubmit)}>
          <OnboardingContentCenter>
            <Text size="medium" color="textPrimary">
              Preencha os dados para se registrar
            </Text>

            <OnboardingInputsContainer>
              <OnboardingNameSurnameContainer>
                <Controller
                  name="firstName"
                  control={registerForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome"
                      variant="outlined"
                      fullWidth
                      error={!!registerForm.formState.errors.firstName}
                      helperText={registerForm.formState.errors.firstName?.message}
                      size="small"
                    />
                  )}
                />

                <Controller
                  name="lastName"
                  control={registerForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sobrenome"
                      variant="outlined"
                      fullWidth
                      error={!!registerForm.formState.errors.lastName}
                      helperText={registerForm.formState.errors.lastName?.message}
                      size="small"
                    />
                  )}
                />
              </OnboardingNameSurnameContainer>

              <Controller
                name="email"
                control={registerForm.control}
                defaultValue={userEmail}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    variant="outlined"
                    fullWidth
                    error={!!registerForm.formState.errors.email}
                    helperText={registerForm.formState.errors.email?.message}
                    size="small"
                    disabled
                  />
                )}
              />

              <Controller
                name="phone"
                control={registerForm.control}
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    country={'br'}
                    specialLabel=""
                    placeholder="Telefone"
                    containerStyle={{
                      width: '100%',
                    }}
                    buttonStyle={{
                      backgroundColor: theme.background.secondary,
                      border: `1px solid ${registerForm.formState.errors.phone ? theme.error : theme.text.primary}`,
                    }}
                    dropdownStyle={{
                      backgroundColor: theme.background.secondary,
                    }}
                    inputStyle={{
                      width: '100%',
                      backgroundColor: theme.background.secondary,
                      border: `1px solid ${registerForm.formState.errors.phone ? theme.error : theme.text.primary}`,
                      color: theme.text.primary,
                    }}
                  />
                )}
              />
              {registerForm.formState.errors.phone && (
                <span
                  style={{
                    color: `${theme.error}`,
                    fontSize: '10px',
                    marginLeft: '1rem',
                    marginTop: '-0.5rem',
                  }}
                >
                  {registerForm.formState.errors.phone.message}
                </span>
              )}

              <Controller
                name="password"
                control={registerForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    error={!!registerForm.formState.errors.password}
                    helperText={registerForm.formState.errors.password?.message}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={toggleShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={registerForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirmar senha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    error={!!registerForm.formState.errors.confirmPassword}
                    helperText={registerForm.formState.errors.confirmPassword?.message}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={toggleShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </OnboardingInputsContainer>

            <Button type="submit" variant="primary" fullWidth loading={loading}>
              {loading ? 'Registrando...' : 'Registrar-se'}
            </Button>

            <Button
              type="button"
              variant="minimal"
              size="medium"
              onClick={() => setCurrentStep('email')}
            >
              Voltar
            </Button>
          </OnboardingContentCenter>
        </OnboardingForm>
      </OnboardingLayout>
    );
  }

  // Event Selection Step
  if (currentStep === 'select-event') {
    return (
      <OnboardingLayout maxWidth="100%" height="100%">
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '40px 20px',
            overflow: 'auto',
            zIndex: 1,
          }}
        >
          <EventSelectionStep
            events={upcomingEvents}
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            onSubscribe={subscribeToEvent}
            loading={loading}
          />
        </Box>
      </OnboardingLayout>
    );
  }

  return null;
};
